import { useEffect, useState } from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TeamTreeNode } from './TeamTree'

interface Member {
  id: number
  label: string
}

interface EditTeamSheetProps {
  team: TeamTreeNode | null
  onOpenChange: (open: boolean) => void
}

export function EditTeamSheet({ team, onOpenChange }: EditTeamSheetProps) {
  const [members, setMembers] = useState<Member[]>([])
  const [loadingMembers, setLoadingMembers] = useState(false)

  const { data, setData, patch, processing, errors, reset } = useForm({
    team: {
      name: '',
      responsible_user_id: '',
    },
  })

  // Fetch members and sync form when team changes
  useEffect(() => {
    if (team) {
      setData('team', {
        name: team.name,
        responsible_user_id: team.responsible_user_id?.toString() ?? '',
      })

      setLoadingMembers(true)
      fetch(`/admin/teams/${team.id}/edit`, {
        headers: { 'Accept': 'application/json' },
      })
        .then((res) => res.json())
        .then((data) => setMembers(data.members ?? []))
        .catch(() => setMembers([]))
        .finally(() => setLoadingMembers(false))
    } else {
      setMembers([])
    }
  }, [team?.id])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!team) return
    patch(`/admin/teams/${team.id}`, {
      onSuccess: () => {
        reset()
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={!!team} onOpenChange={onOpenChange}>
      <SheetContent className="w-[540px] sm:max-w-[540px]">
        <SheetHeader>
          <SheetTitle>Edit Team</SheetTitle>
          <SheetDescription>
            Update team name and responsible user.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="edit_team_name">Name</Label>
            <Input
              id="edit_team_name"
              value={data.team.name}
              onChange={(e) => setData('team', { ...data.team, name: e.target.value })}
              required
              autoFocus
            />
            {errors['team.name'] && (
              <p className="text-sm text-destructive">{errors['team.name']}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit_responsible_user">Responsible User</Label>
            {loadingMembers ? (
              <p className="text-sm text-muted-foreground">Loading members...</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members in this team</p>
            ) : (
              <Select
                value={data.team.responsible_user_id}
                onValueChange={(val) => setData('team', { ...data.team, responsible_user_id: val })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id.toString()}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <Button type="submit" disabled={processing} className="w-full">
            {processing ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}
